/**
 * Offer surfaces — toast + campaign interstitial (mobile UX program,
 * Phase 5, §6.5 O1–O5; behind ui.mobileV2).
 *
 * One family with the Card (pilot-prompt): same offer data (O2 fields:
 * imageUrl / headline / message / CTA), three escalating weights:
 *   card (M3) → toast (O1) → interstitial (O2, campaign-triggered ONLY).
 *
 * O1 toast grammar: greeting + optional media banner + message +
 * explicit Accept / "No, thanks" pair. Decline is a FIRST-CLASS outcome
 * (more signal than a dismiss ×). A declined/unacknowledged toast
 * collapses to a badge on the launcher — the offer stays reachable
 * without re-pushing (pacing-friendly persistence, budget cost 0).
 *
 * O2 interstitial: the ONE sanctioned takeover — media + headline +
 * message + optional lead capture. Always dismissible (×, ESC, scrim).
 * Scroll-lock WITHOUT touching the host <body> (CSS-isolation law):
 * a fixed overlay + overscroll-behavior:contain + touch-action:none on
 * the scrim; the panel itself scrolls.
 */

import { render } from 'preact';
import type { OfferDesignData } from '../api';
import { useEffect, useState } from 'preact/hooks';

export interface OfferSurfacePayload {
  key: string;
  agentName: string;
  avatarUrl: string | null;
  imageUrl?: string | null;
  headline?: string | null;
  message: string;
  ctaLabel?: string | null;
  primaryColor?: string;
  /** Offers v2 — the creative as data (AI designer output). */
  design?: OfferDesignData | null;
  /** v3 — identified visitor: skip the email field (one-tap accept)
   *  and greet by first name. Set from discover's offerIdentity hint;
   *  the email itself never rides the wire. */
  identified?: boolean;
  firstName?: string | null;
}

/** "Hi {name} — " prefix for identified visitors. Skipped when the
 *  authored copy already opens with its own greeting. */
function greetPrefix(payload: OfferSurfacePayload): string {
  if (!payload.identified || !payload.firstName) return '';
  if (/^\s*(hi|hey|hello|welcome)\b/i.test(payload.message)) return '';
  return `Hi ${payload.firstName} — `;
}

/**
 * Offers v2 — inline style layers from the design data. The design is
 * DATA (same shape the admin preview renders), so both surfaces stay
 * pixel-consistent with authoring. Absent fields fall back to the
 * default card look. Full layout variants land with the layout
 * mirrors; v1 applies background / text / button.
 */
/**
 * Inline emphasis in offer copy — **bold**, *italic*, <u>underline</u>.
 * Grammar mirrors the admin editor's renderInlineMarkup; keep identical.
 */
const INLINE_TOKEN = /(\*\*[^*]+\*\*|\*[^*]+\*|<u>[^<]+<\/u>)/g;

function renderInline(text: string): (string | preact.JSX.Element)[] {
  return text.split(INLINE_TOKEN).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <b key={i}>{part.slice(2, -2)}</b>;
    if (part.startsWith('<u>') && part.endsWith('</u>')) return <u key={i}>{part.slice(3, -4)}</u>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <i key={i}>{part.slice(1, -1)}</i>;
    }
    return part;
  });
}

/** Plain-text form for chat morphs (chat bubbles render plain text). */
export function stripInlineMarkup(text: string): string {
  return text.replace(INLINE_TOKEN, (m) => {
    if (m.startsWith('**')) return m.slice(2, -2);
    if (m.startsWith('<u>')) return m.slice(3, -4);
    return m.slice(1, -1);
  });
}

/** Background layers (image+scrim or flat color) from the design data. */
function bgLayers(design: OfferDesignData | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const bg = design?.background;
  if (bg?.imageUrl) {
    const scrim = Math.min(1, Math.max(0, bg.scrim ?? 0.45));
    out.backgroundImage =
      'linear-gradient(rgba(0,0,0,' + scrim + '), rgba(0,0,0,' + scrim + ')), url("' +
      bg.imageUrl + '")';
    out.backgroundSize = 'cover';
    out.backgroundPosition = 'center';
  } else if (bg?.color) {
    out.background = bg.color;
  }
  return out;
}

/** Card width per design.size — inline so it outranks the sp- classes. */
function sizeWidth(
  design: OfferDesignData | null | undefined,
  surface: 'toast' | 'popup' | 'split',
): Record<string, string> {
  const size = design?.size;
  if (!size || size === 'md') return {};
  const px =
    surface === 'toast'
      ? { sm: 260, lg: 344 }[size]
      : surface === 'split'
        ? { sm: 440, lg: 620 }[size]
        : { sm: 340, lg: 500 }[size];
  return px ? { width: px + 'px', maxWidth: 'calc(100vw - 32px)' } : {};
}

function designStyles(design: OfferDesignData | null | undefined): {
  panel: Record<string, string>;
  headline: Record<string, string>;
  message: Record<string, string>;
  button: Record<string, string>;
} {
  if (!design) return { panel: {}, headline: {}, message: {}, button: {} };
  // split/event repaint the MEDIA region with the design background and
  // keep the panel itself neutral (split: bodyTint on the copy panel;
  // event: white notification card around the typographic block).
  const mediaLayout = design.layout === 'split' || design.layout === 'event';
  const panel: Record<string, string> = mediaLayout ? {} : bgLayers(design);
  if (design.text?.color && !mediaLayout) panel.color = design.text.color;
  if (design.text?.fontFamily) panel.fontFamily = design.text.fontFamily;
  const headline: Record<string, string> = {};
  const message: Record<string, string> = {};
  // event keeps the surrounding card neutral — the authored text color
  // belongs to the typographic media block, not the notification copy.
  if (design.text?.color && design.layout !== 'event') {
    headline.color = design.text.color;
    // The base message color is a muted gray tuned for white cards —
    // designed panels need the authored color at slight transparency.
    message.color = design.text.color;
    message.opacity = '0.88';
  }
  if (design.text?.headlineScale === 'lg') headline.fontSize = '28px';
  if (design.text?.headlineScale === 'xl') headline.fontSize = '36px';
  const button: Record<string, string> = {};
  if (design.button?.background) button.background = design.button.background;
  if (design.button?.color) button.color = design.button.color;
  if (typeof design.button?.radiusPx === 'number') {
    button.borderRadius = design.button.radiusPx + 'px';
  }
  return { panel, headline, message, button };
}

/**
 * v3 'event' layout — the typographic media block (D8): eyebrow +
 * display headline INSIDE the designed media area. The notification
 * copy + actions stay on the neutral card around it.
 */
function EventBlock({ payload }: { payload: OfferSurfacePayload }) {
  const design = payload.design;
  const layers = bgLayers(design);
  // Always a designed surface: authored background → offer image under
  // a scrim → a deep brand gradient.
  if (!layers.background && !layers.backgroundImage) {
    if (payload.imageUrl) {
      layers.backgroundImage =
        'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45)), url("' + payload.imageUrl + '")';
      layers.backgroundSize = 'cover';
      layers.backgroundPosition = 'center';
    } else {
      layers.background =
        'linear-gradient(120deg,#101828 0%,var(--sp-offer-primary) 130%)';
    }
  }
  return (
    <div class="sp-offer-evblock" style={{ ...layers, color: design?.text?.color ?? '#ffffff' }}>
      {design?.eyebrow ? <div class="sp-offer-eyebrow">{design.eyebrow}</div> : null}
      {payload.headline ? <div class="sp-offer-evblock-h">{payload.headline}</div> : null}
    </div>
  );
}

export interface OfferToastHandlers {
  /** Visitor tapped Accept (or the CTA label) — same morph path as the Card. */
  onAccept: () => void;
  /** Explicit "No, thanks" — first-class decline outcome (O1). */
  onDecline: () => void;
  /** Collapse to the launcher badge (× / timeout) — offer stays reachable. */
  onCollapse: () => void;
}

function Avatar({ url, name }: { url: string | null; name: string }) {
  if (url) return <img class="sp-offer-avatar" src={url} alt="" />;
  return <span class="sp-offer-avatar sp-offer-avatar--initial">{name.slice(0, 1)}</span>;
}

function Toast({
  payload,
  handlers,
}: {
  payload: OfferSurfacePayload;
  handlers: OfferToastHandlers;
}) {
  const styles = designStyles(payload.design);
  return (
    <div
      class="sp-offer-toast"
      role="status"
      data-sp-entrance={payload.design?.entrance ?? 'slide-up'}
      style={{
        ...(payload.primaryColor
          ? ({ ['--sp-offer-primary' as string]: payload.primaryColor } as Record<string, string>)
          : {}),
        ...styles.panel,
        ...sizeWidth(payload.design, 'toast'),
      }}
    >
      <button
        type="button"
        class="sp-offer-close"
        aria-label="Dismiss"
        onClick={handlers.onCollapse}
      >
        ×
      </button>
      <div class="sp-offer-toast-head">
        <Avatar url={payload.avatarUrl} name={payload.agentName} />
        <span
          class="sp-offer-toast-name"
          style={styles.headline.color ? { color: styles.headline.color } : undefined}
        >
          {payload.agentName}
        </span>
      </div>
      {payload.design?.layout === 'event' ? (
        // D8 grammar — copy first, then the typographic media block.
        <>
          <div class="sp-offer-message" style={styles.message}>
            {greetPrefix(payload)}
            {renderInline(payload.message)}
          </div>
          <EventBlock payload={payload} />
        </>
      ) : (
        <>
          {payload.imageUrl ? (
            <img class="sp-offer-media" src={payload.imageUrl} alt="" />
          ) : null}
          {payload.design?.eyebrow ? (
            <div class="sp-offer-eyebrow" style={styles.message}>{payload.design.eyebrow}</div>
          ) : null}
          {payload.headline ? (
            <div
              class="sp-offer-headline"
              style={styles.headline.color ? { color: styles.headline.color } : undefined}
            >
              {payload.headline}
            </div>
          ) : null}
          <div class="sp-offer-message" style={styles.message}>
            {greetPrefix(payload)}
            {renderInline(payload.message)}
          </div>
        </>
      )}
      <div class="sp-offer-actions">
        <button type="button" class="sp-offer-accept" style={styles.button} onClick={handlers.onAccept}>
          {payload.ctaLabel ?? 'Take a look'}
        </button>
        <button type="button" class="sp-offer-decline" onClick={handlers.onDecline}>
          {payload.design?.declineLabel ?? 'No, thanks'}
        </button>
      </div>
    </div>
  );
}

export interface OfferInterstitialHandlers {
  onAccept: (email?: string) => void;
  onDismiss: () => void;
}

function Interstitial({
  payload,
  handlers,
  captureEmail,
}: {
  payload: OfferSurfacePayload;
  handlers: OfferInterstitialHandlers;
  captureEmail: boolean;
}) {
  const [email, setEmail] = useState('');
  const styles = designStyles(payload.design);
  const design = payload.design;
  const split = design?.layout === 'split';
  // Identified visitors never re-type the email — one-tap accept; the
  // server already knows who they are (fingerprint → visitor).
  const wantsEmail = captureEmail && !payload.identified;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handlers.onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const emailField = wantsEmail ? (
    <input
      type="email"
      class="sp-offer-email"
      placeholder="Work email"
      value={email}
      onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
    />
  ) : null;
  const acceptBtn = (
    <button
      type="button"
      class="sp-offer-accept sp-offer-accept--lg"
      style={styles.button}
      onClick={() => handlers.onAccept(wantsEmail ? email.trim() || undefined : undefined)}
    >
      {payload.ctaLabel ?? 'Register'}
    </button>
  );
  // D9 split — media panel + tinted copy panel + agent identity chip.
  const splitMedia = (() => {
    if (!split) return {};
    const layers = bgLayers(design);
    if (!layers.background && !layers.backgroundImage) {
      if (payload.imageUrl) {
        layers.backgroundImage = 'url("' + payload.imageUrl + '")';
        layers.backgroundSize = 'cover';
        layers.backgroundPosition = 'center';
      } else {
        layers.background = 'linear-gradient(160deg,var(--sp-offer-primary) 0%,#101828 100%)';
      }
    }
    return layers;
  })();
  return (
    <div
      class="sp-offer-interstitial"
      data-sp-entrance={payload.design?.entrance ?? 'slide-up'}
      style={
        payload.primaryColor
          ? ({ ['--sp-offer-primary' as string]: payload.primaryColor } as Record<string, string>)
          : undefined
      }
    >
      {/* Scrim — tap dismisses; owns the scroll lock (overscroll contain,
          no host-body mutation). */}
      <div class="sp-offer-scrim" onClick={handlers.onDismiss} />
      <div
        class={split ? 'sp-offer-panel sp-offer-panel--split' : 'sp-offer-panel'}
        role="dialog"
        aria-modal="true"
        style={{ ...styles.panel, ...sizeWidth(design, split ? 'split' : 'popup') }}
      >
        <button
          type="button"
          class="sp-offer-close"
          aria-label="Dismiss"
          onClick={handlers.onDismiss}
        >
          ×
        </button>
        {split ? (
          <>
            <div class="sp-offer-split-media" style={splitMedia} />
            <div
              class="sp-offer-split-body"
              style={{
                background: design?.bodyTint ?? '#ffffff',
                ...(design?.text?.color ? { color: design.text.color } : {}),
                ...(design?.text?.fontFamily ? { fontFamily: design.text.fontFamily } : {}),
              }}
            >
              {design?.eyebrow ? (
                <div
                  class="sp-offer-eyebrow"
                  style={{ color: design?.button?.background ?? 'var(--sp-offer-primary)' }}
                >
                  {design.eyebrow}
                </div>
              ) : null}
              {payload.headline ? (
                <div class="sp-offer-headline sp-offer-headline--lg" style={styles.headline}>
                  {payload.headline}
                </div>
              ) : null}
              <div class="sp-offer-message" style={styles.message}>
                {greetPrefix(payload)}
                {renderInline(payload.message)}
              </div>
              {emailField}
              {acceptBtn}
              <div class="sp-offer-agentchip">
                <Avatar url={payload.avatarUrl} name={payload.agentName} />
                <span>{payload.agentName}</span>
              </div>
            </div>
          </>
        ) : (
          <>
            {design?.layout !== 'event' &&
            payload.imageUrl &&
            !design?.background?.imageUrl ? (
              <img class="sp-offer-media sp-offer-media--hero" src={payload.imageUrl} alt="" />
            ) : null}
            <div class="sp-offer-panel-body">
              <div class="sp-offer-toast-head">
                <Avatar url={payload.avatarUrl} name={payload.agentName} />
                <span class="sp-offer-toast-name" style={styles.headline.color ? { color: styles.headline.color } : undefined}>
                  {payload.agentName}
                </span>
              </div>
              {design?.layout === 'event' ? (
                <EventBlock payload={payload} />
              ) : (
                <>
                  {design?.eyebrow ? (
                    <div class="sp-offer-eyebrow" style={styles.message}>{design.eyebrow}</div>
                  ) : null}
                  {payload.headline ? (
                    <div class="sp-offer-headline sp-offer-headline--lg" style={styles.headline}>
                      {payload.headline}
                    </div>
                  ) : null}
                </>
              )}
              <div class="sp-offer-message" style={styles.message}>
                {greetPrefix(payload)}
                {renderInline(payload.message)}
              </div>
              {emailField}
              {acceptBtn}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export interface OfferBannerHandlers {
  onAccept: () => void;
  onDismiss: () => void;
}

function Banner({
  payload,
  handlers,
}: {
  payload: OfferSurfacePayload;
  handlers: OfferBannerHandlers;
}) {
  const styles = designStyles(payload.design);
  return (
    <div
      class="sp-offer-banner"
      role="status"
      data-sp-entrance={payload.design?.entrance ?? 'slide-up'}
      style={{
        ...(payload.primaryColor
          ? ({ ['--sp-offer-primary' as string]: payload.primaryColor } as Record<string, string>)
          : {}),
        ...styles.panel,
      }}
    >
      <Avatar url={payload.avatarUrl} name={payload.agentName} />
      <span class="sp-offer-banner-copy" style={styles.message}>
        {payload.headline ? <b style={styles.headline.color ? { color: styles.headline.color } : undefined}>{payload.headline} </b> : null}
        {renderInline(payload.message)}
      </span>
      <button type="button" class="sp-offer-accept sp-offer-accept--banner" style={styles.button} onClick={handlers.onAccept}>
        {payload.ctaLabel ?? 'Take a look'}
      </button>
      <button type="button" class="sp-offer-close sp-offer-close--banner" aria-label="Dismiss" onClick={handlers.onDismiss}>
        ×
      </button>
    </div>
  );
}

export interface OfferSurfaceController {
  close(): void;
}

function mountInto(parent: HTMLElement, cls: string): {
  anchor: HTMLElement;
  close: () => void;
} {
  const anchor = document.createElement('div');
  anchor.className = cls;
  parent.appendChild(anchor);
  return {
    anchor,
    close: () => {
      render(null, anchor);
      anchor.remove();
    },
  };
}

/** O1 — mount the offer toast (weight 1 of the pacing budget). */
export function mountOfferToast(
  payload: OfferSurfacePayload,
  handlers: OfferToastHandlers,
  parent: HTMLElement = document.body,
): OfferSurfaceController {
  const { anchor, close } = mountInto(parent, 'sp-offer-toast-anchor');
  const wrapped: OfferToastHandlers = {
    onAccept: () => {
      handlers.onAccept();
      close();
    },
    onDecline: () => {
      handlers.onDecline();
      close();
    },
    onCollapse: () => {
      handlers.onCollapse();
      close();
    },
  };
  render(<Toast payload={payload} handlers={wrapped} />, anchor);
  return { close };
}

/** Banner — top strip across the page (weight 1, ambient like the toast).
 *
 * Hello-bar semantics: the strip PUSHES the site down instead of
 * covering it. This is the one sanctioned host-page mutation (a single
 * reversible inline margin-top on <html> — no classes, no <body>
 * touch): static/sticky headers flow down correctly; a rare
 * position:fixed header keeps its own coordinates and rides under the
 * strip, which is the industry-standard hello-bar trade-off. */
export function mountOfferBanner(
  payload: OfferSurfacePayload,
  handlers: OfferBannerHandlers,
  parent: HTMLElement = document.body,
): OfferSurfaceController {
  const { anchor, close: closeAnchor } = mountInto(parent, 'sp-offer-banner-anchor');
  const html = document.documentElement;
  const priorMargin = html.style.marginTop;
  const release = (): void => {
    html.style.marginTop = priorMargin;
  };
  const close = (): void => {
    release();
    closeAnchor();
  };
  const wrapped: OfferBannerHandlers = {
    onAccept: () => {
      handlers.onAccept();
      close();
    },
    onDismiss: () => {
      handlers.onDismiss();
      close();
    },
  };
  render(<Banner payload={payload} handlers={wrapped} />, anchor);
  // Measure after paint — the strip's height depends on copy + device.
  requestAnimationFrame(() => {
    const el = anchor.querySelector<HTMLElement>('.sp-offer-banner');
    if (el) html.style.marginTop = `${el.offsetHeight}px`;
  });
  return { close };
}

/** O2 — mount the campaign interstitial (weight 2 = the whole budget, O5). */
export function mountOfferInterstitial(
  payload: OfferSurfacePayload,
  handlers: OfferInterstitialHandlers,
  options: { captureEmail?: boolean; parent?: HTMLElement } = {},
): OfferSurfaceController {
  const { anchor, close } = mountInto(
    options.parent ?? document.body,
    'sp-offer-interstitial-anchor',
  );
  const wrapped: OfferInterstitialHandlers = {
    onAccept: (email) => {
      handlers.onAccept(email);
      close();
    },
    onDismiss: () => {
      handlers.onDismiss();
      close();
    },
  };
  render(
    <Interstitial
      payload={payload}
      handlers={wrapped}
      captureEmail={options.captureEmail ?? true}
    />,
    anchor,
  );
  return { close };
}
